import { governanceExecutionIntentStates, governanceReadOnlyModes } from './types';

const generatedAt = '2026-05-29T15:00:00.000Z';

function metadata(readModelId, tenantId, freshness = 'fresh') {
  return {
    readModelId,
    tenantId,
    sourceVersion: 'governance-readonly-mock/v1',
    generatedAt,
    lastSourceEventAt: '2026-05-29T14:42:00.000Z',
    freshness,
    consistency: 'snapshot',
    indexCheckpointId: `mock-checkpoint-${tenantId}`,
    correlationId: null,
    source: governanceReadOnlyModes.LOCAL_MOCK,
  };
}

export const governanceReadOnlyTenantMappings = {
  'tenant-root-axodus': 'tenant-root-axodus',
  'tenant-subdao-governance-labs': 'tenant-governance-labs',
  'tenant-company-bba': 'tenant-bba-agency',
};

export const governanceReadOnlyFixtures = {
  tenants: {
    'tenant-root-axodus': {
      summary: {
        metadata: metadata('tenant-summary-root', 'tenant-root-axodus'),
        tenantId: 'tenant-root-axodus',
        proposalCountsByStatus: { approved: 2, voting: 1, queued: 1, blocked: 1 },
        openReviews: 2,
        pendingDecisions: 1,
        activeEmergencyActions: 1,
        blockedExecutionIntents: 2,
        latestDecisionAt: '2026-05-29T12:20:00.000Z',
        latestAuditAt: '2026-05-29T14:42:00.000Z',
      },
      proposals: [
        {
          proposalId: 'gov-prop-core-001',
          tenantId: 'tenant-root-axodus',
          title: 'Ratify read-only Governance backend gate',
          summary: 'Formalizes the local/mock read-only backend foundation as safe for AxodusAPP planning.',
          status: 'approved',
          proposerActorId: 'actor-governance-council',
          createdAt: '2026-05-28T10:00:00.000Z',
          updatedAt: '2026-05-29T12:20:00.000Z',
          submittedAt: '2026-05-28T11:00:00.000Z',
          decisionStatus: 'approved',
          reviewStatus: 'complete',
          emergencyFlag: false,
          executionIntentStatus: governanceExecutionIntentStates.BLOCKED,
          latestVersion: 2,
          tags: ['read-only', 'backend-gate', 'axodusapp'],
        },
        {
          proposalId: 'gov-prop-core-002',
          tenantId: 'tenant-root-axodus',
          title: 'Classify emergency notices for frontend read-only display',
          summary: 'Defines sanitized emergency notice visibility without exposing incident evidence.',
          status: 'voting',
          proposerActorId: 'actor-security-review',
          createdAt: '2026-05-29T08:30:00.000Z',
          updatedAt: '2026-05-29T13:15:00.000Z',
          submittedAt: '2026-05-29T09:00:00.000Z',
          decisionStatus: null,
          reviewStatus: 'in-review',
          emergencyFlag: true,
          executionIntentStatus: governanceExecutionIntentStates.NONE,
          latestVersion: 1,
          tags: ['emergency', 'sanitization'],
        },
      ],
      emergencyActions: [
        {
          emergencyActionId: 'emergency-root-001',
          tenantId: 'tenant-root-axodus',
          proposalId: 'gov-prop-core-002',
          actionType: 'READ_ONLY_NOTICE',
          status: 'active',
          severity: 'warning',
          reason: 'Sanitized governance emergency notice. Sensitive evidence is intentionally excluded.',
          createdAt: '2026-05-29T13:00:00.000Z',
          expiresAt: '2026-06-05T13:00:00.000Z',
          ratificationStatus: 'pending',
          auditId: 'audit-root-003',
        },
      ],
      timeline: [
        {
          timelineId: 'timeline-root-002',
          tenantId: 'tenant-root-axodus',
          entityType: 'proposal',
          entityId: 'gov-prop-core-002',
          eventType: 'proposal.review_requested',
          actorId: 'actor-security-review',
          occurredAt: '2026-05-29T13:15:00.000Z',
          summary: 'Emergency notice review requested for sanitized frontend visibility.',
          severity: 'warning',
          auditId: 'audit-root-003',
          decisionReference: null,
          receiptReference: null,
        },
        {
          timelineId: 'timeline-root-001',
          tenantId: 'tenant-root-axodus',
          entityType: 'proposal',
          entityId: 'gov-prop-core-001',
          eventType: 'decision.recorded',
          actorId: 'actor-governance-council',
          occurredAt: '2026-05-29T12:20:00.000Z',
          summary: 'Read-only backend gate approved for AxodusAPP planning.',
          severity: 'info',
          auditId: 'audit-root-001',
          decisionReference: 'decision-root-001',
          receiptReference: null,
        },
      ],
      decisions: [
        {
          decisionId: 'decision-root-001',
          proposalId: 'gov-prop-core-001',
          tenantId: 'tenant-root-axodus',
          decisionType: 'gate-approval',
          outcome: 'approved',
          authority: 'Governance Council',
          decidedAt: '2026-05-29T12:20:00.000Z',
          evidenceReferences: ['evidence-reference-redacted'],
          executionImpact: 'record_only',
          auditId: 'audit-root-001',
        },
      ],
    },
    'tenant-governance-labs': {
      summary: {
        metadata: metadata('tenant-summary-governance-labs', 'tenant-governance-labs', 'stale'),
        tenantId: 'tenant-governance-labs',
        proposalCountsByStatus: { draft: 1, queued: 1, blocked: 1 },
        openReviews: 1,
        pendingDecisions: 1,
        activeEmergencyActions: 0,
        blockedExecutionIntents: 1,
        latestDecisionAt: null,
        latestAuditAt: '2026-05-28T16:12:00.000Z',
      },
      proposals: [
        {
          proposalId: 'gov-prop-labs-001',
          tenantId: 'tenant-governance-labs',
          title: 'Prepare tenant read model fixture contract',
          summary: 'Drafts a local fixture shape for sub-DAO read-only views.',
          status: 'queued',
          proposerActorId: 'actor-labs-operator',
          createdAt: '2026-05-28T15:00:00.000Z',
          updatedAt: '2026-05-28T16:12:00.000Z',
          submittedAt: '2026-05-28T15:30:00.000Z',
          decisionStatus: null,
          reviewStatus: 'pending',
          emergencyFlag: false,
          executionIntentStatus: governanceExecutionIntentStates.RECORDED,
          latestVersion: 1,
          tags: ['tenant', 'fixtures'],
        },
      ],
      emergencyActions: [],
      timeline: [],
      decisions: [],
    },
    'tenant-bba-agency': {
      summary: {
        metadata: metadata('tenant-summary-bba', 'tenant-bba-agency', 'unknown'),
        tenantId: 'tenant-bba-agency',
        proposalCountsByStatus: {},
        openReviews: 0,
        pendingDecisions: 0,
        activeEmergencyActions: 0,
        blockedExecutionIntents: 0,
        latestDecisionAt: null,
        latestAuditAt: null,
      },
      proposals: [],
      emergencyActions: [],
      timeline: [],
      decisions: [],
    },
  },
};
